/**
 * SiteRenderer — renders a website JSON (theme + pages + sections) as premium React.
 * Used both by the Website Builder preview AND by the public /site/:slug route.
 */
import { motion } from "framer-motion";
import { ChevronRight, Check, Star, Mail } from "lucide-react";

const FONTS = {
  Manrope: "'Manrope', system-ui, sans-serif",
  Unbounded: "'Unbounded', system-ui, sans-serif",
  Inter: "'Inter', system-ui, sans-serif",
};

const RADIUS = { none: "0", sm: "6px", md: "12px", lg: "24px" };

export default function SiteRenderer({ site, embedded = false }) {
  const theme = site?.theme || {};
  const primary = theme.primary || "#00E5FF";
  const accent = theme.accent || "#0077FF";
  const fontFamily = FONTS[theme.font] || FONTS.Manrope;
  const radius = RADIUS[theme.radius] || RADIUS.sm;

  const page = site?.pages?.[0] || { sections: [] };

  return (
    <div
      className="min-h-screen bg-[#050505] text-white"
      style={{ fontFamily, "--kr-primary": primary, "--kr-accent": accent, "--kr-radius": radius }}
      data-testid="site-renderer"
    >
      {(page.sections || []).map((s, i) => (
        <Section key={s.id || i} section={s} primary={primary} accent={accent} radius={radius} embedded={embedded} />
      ))}
    </div>
  );
}

function Section({ section, primary, accent, radius, embedded }) {
  const t = section.type;
  const p = section.props || {};
  const wrap = "max-w-6xl mx-auto px-6";
  const padY = embedded ? "py-16" : "py-24";

  if (t === "hero") {
    return (
      <section className={`${wrap} ${padY}`}>
        <div className="grid grid-cols-12 gap-8 items-center">
          <div className="col-span-12 lg:col-span-7">
            {p.eyebrow && <div className="text-xs uppercase tracking-[0.25em]" style={{ color: primary }}>{p.eyebrow}</div>}
            <h1 className="mt-4 text-5xl sm:text-6xl font-bold leading-[1.05] tracking-tight">{p.headline || "Untitled"}</h1>
            {p.subhead && <p className="mt-6 text-white/60 text-lg max-w-xl leading-relaxed">{p.subhead}</p>}
            <div className="mt-8 flex gap-3">
              {p.cta_primary && <button className="px-6 py-3 font-semibold text-black" style={{ background: primary, borderRadius: radius }}>{p.cta_primary}</button>}
              {p.cta_secondary && <button className="px-6 py-3 border border-white/15 hover:bg-white/5" style={{ borderRadius: radius }}>{p.cta_secondary}</button>}
            </div>
          </div>
          <div className="col-span-12 lg:col-span-5 relative">
            <div className="aspect-square rounded-3xl relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${primary}22, ${accent}22)`, borderRadius: radius }}>
              <div className="absolute inset-0" style={{ background: `radial-gradient(circle at 30% 30%, ${primary}40, transparent 60%)` }} />
              <div className="absolute inset-0" style={{ background: `radial-gradient(circle at 70% 70%, ${accent}40, transparent 55%)` }} />
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (t === "features" || t === "services") {
    const items = p.items || [];
    return (
      <section className={`${wrap} ${padY}`}>
        {p.title && <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">{p.title}</h2>}
        {p.subtitle && <p className="mt-3 text-white/60 max-w-2xl">{p.subtitle}</p>}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it, i) => (
            <div key={i} className="p-6 border border-white/8" style={{ background: "rgba(255,255,255,0.02)", borderRadius: radius }}>
              <div className="w-10 h-10 flex items-center justify-center border border-white/10" style={{ color: primary, borderRadius: radius }}>
                <Star className="h-5 w-5" strokeWidth={1.5} />
              </div>
              <div className="mt-5 text-lg font-semibold">{it.title}</div>
              <div className="mt-2 text-sm text-white/55">{it.body}</div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (t === "testimonials") {
    const items = p.items || [];
    return (
      <section className={`${wrap} ${padY}`}>
        {p.title && <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">{p.title}</h2>}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((it, i) => (
            <div key={i} className="p-6 border border-white/8" style={{ background: "rgba(255,255,255,0.02)", borderRadius: radius }}>
              <div className="text-white/85 italic">"{it.quote}"</div>
              <div className="mt-4 text-sm text-white/60">— {it.author}<span className="text-white/40">{it.role ? `, ${it.role}` : ""}</span></div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (t === "pricing") {
    const tiers = p.tiers || [];
    return (
      <section className={`${wrap} ${padY}`}>
        {p.title && <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">{p.title}</h2>}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          {tiers.map((t, i) => (
            <div key={i} className="p-6 border border-white/8 flex flex-col" style={{ background: "rgba(255,255,255,0.02)", borderRadius: radius }}>
              <div className="text-lg font-semibold">{t.name}</div>
              <div className="mt-3 text-3xl font-bold">{t.price}</div>
              <ul className="mt-5 space-y-2 flex-1">
                {(t.features || []).map((f, j) => (
                  <li key={j} className="flex items-start gap-2 text-sm text-white/70"><Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: primary }} /> {f}</li>
                ))}
              </ul>
              <button className="mt-6 px-5 py-2.5 font-semibold text-black" style={{ background: primary, borderRadius: radius }}>Get started</button>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (t === "about") {
    return (
      <section className={`${wrap} ${padY}`}>
        <div className="grid grid-cols-12 gap-8 items-center">
          <div className="col-span-12 md:col-span-6">
            {p.title && <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">{p.title}</h2>}
            {p.body && <p className="mt-6 text-white/65 leading-relaxed">{p.body}</p>}
          </div>
          <div className="col-span-12 md:col-span-6">
            <div className="aspect-video border border-white/8 rounded-lg" style={{ background: `linear-gradient(120deg, ${primary}22, ${accent}22)`, borderRadius: radius }} />
          </div>
        </div>
      </section>
    );
  }

  if (t === "cta") {
    return (
      <section className={`${wrap} ${padY}`}>
        <div className="p-10 md:p-16 border border-white/8 relative overflow-hidden" style={{ borderRadius: radius }}>
          <div className="absolute inset-0" style={{ background: `radial-gradient(circle at 30% 40%, ${primary}22, transparent 60%), radial-gradient(circle at 70% 60%, ${accent}22, transparent 55%)` }} />
          <div className="relative">
            <h3 className="text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl">{p.headline || "Ready to get started?"}</h3>
            {p.subhead && <p className="mt-4 text-white/60 max-w-xl">{p.subhead}</p>}
            <button className="mt-8 px-8 py-3 font-semibold text-black" style={{ background: primary, borderRadius: radius }}>{p.cta || "Get started"}</button>
          </div>
        </div>
      </section>
    );
  }

  if (t === "faq") {
    const items = p.items || [];
    return (
      <section className={`${wrap} ${padY}`}>
        {p.title && <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">{p.title}</h2>}
        <div className="mt-10 divide-y divide-white/8">
          {items.map((q, i) => (
            <details key={i} className="py-4 group">
              <summary className="cursor-pointer flex items-center justify-between text-white/90">{q.q}<ChevronRight className="h-4 w-4 text-white/40 group-open:rotate-90 transition" /></summary>
              <p className="mt-3 text-white/60 text-sm">{q.a}</p>
            </details>
          ))}
        </div>
      </section>
    );
  }

  if (t === "contact") {
    return (
      <section className={`${wrap} ${padY}`}>
        {p.title && <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">{p.title}</h2>}
        <div className="mt-8 max-w-xl">
          <input placeholder="Your email" className="w-full bg-transparent border border-white/10 px-4 py-3" style={{ borderRadius: radius }} />
          <textarea placeholder="Message" rows={5} className="w-full mt-3 bg-transparent border border-white/10 px-4 py-3" style={{ borderRadius: radius }} />
          <button className="mt-3 px-6 py-3 font-semibold text-black" style={{ background: primary, borderRadius: radius }}><Mail className="inline h-4 w-4 mr-2" /> Send</button>
        </div>
      </section>
    );
  }

  if (t === "footer") {
    return (
      <footer className={`${wrap} py-10 border-t border-white/8 mt-16`}>
        <div className="flex flex-wrap items-center justify-between gap-4 text-sm text-white/50">
          <div>{p.tagline || p.copyright || "© 2026"}</div>
          <div className="flex gap-4">{(p.links || []).map((l, i) => <a key={i} href="#" className="hover:text-white">{l}</a>)}</div>
        </div>
      </footer>
    );
  }

  return (
    <section className={`${wrap} ${padY} text-white/40 text-sm`}>
      Unsupported section type: <code className="font-mono">{t}</code>
    </section>
  );
}
